import React, { useEffect, useState, useRef } from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { AiOutlineCheck, AiOutlineWarning, AiFillFileAdd } from "react-icons/ai";
import CreateChartNotesModal from "../../../Common/popUpModals/CreateChartNotesModal";
import { replaceNextLineCharWithNextLineTag } from "../../../../util/common";
import ReactHtmlParser from "react-html-parser";
import npis from "../../../../util/npis.json";
import { MonitorService } from "../../../../util/captureException";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "jspdf-autotable";

const getHighlightedText = (text, highlight) => {
  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} className="highlight">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  );
};

// Basic Levenshtein distance function
const levenshtein = (a, b) => {
  const m = a.length,
    n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
  }
  return dp[m][n];
};

const REQUIRED_MATCHES = 2;
const FUZZY_MATCH_THRESHOLD = 0.3;

export default function SingleChart({
  search,
  chartStatus,
  referenceId,
  isClinicalRequired,
  createdDate,
  chartDetails,
  authorName,
  noteMessage,
  patientDetail,
  cb,
  isExpanded: isExpandedProp
}) {
  // Ensure isExpanded is always a boolean
  const [isExpanded, setIsExpanded] = useState(Boolean(isExpandedProp));
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [showCreateChartNotesModal, setShowCreateChartNotesModal] = useState(false);
  const contentRef = useRef(null);
  const [isClone, setIsClone] = useState(false);

  // Returns the NPI for a given full name, or "NPI not found" if no match.
  const getNpiFromName = (fullName, noteType) => {
    if (!fullName) return "NPI not found";

    const normalize = str =>
      str
        .toLowerCase()
        .replace(/[^a-z\s]/gi, "")
        .replace(/\s+/g, " ")
        .trim();

    const nameTokens = normalize(fullName).split(" ");

    const foundEntry = npis.find(entry => {
      const entryTokens = normalize(entry.name).split(" ");
      let matches = 0;
      nameTokens.forEach(inputToken => {
        for (const entryToken of entryTokens) {
          const distance = levenshtein(inputToken, entryToken);
          const threshold = Math.floor(Math.max(inputToken.length, entryToken.length) * FUZZY_MATCH_THRESHOLD); // Allow ~30% difference
          if (distance <= threshold) {
            matches++;
            break;
          }
        }
      });
      return matches >= REQUIRED_MATCHES; // require 2 close matches
    });

    if (!foundEntry && noteType === "BELUGA_HEALTH") {
      MonitorService.captureException(new Error(`NPI not found for name: ${fullName}`));
      return "NPI not found";
    }

    return foundEntry?.npi || "NPI not found";
  };
  const onClickChevronDown = async () => {
    try {
      setIsExpanded(true);
    } catch (error) {
      setLoading(false);
    }
  };
  const searchInNotes = () => {
    if (noteMessage && search && noteMessage.toLowerCase().includes(search.toLowerCase())) {
      return true;
    } else {
      return false;
    }
  };
  const onClickCloneNote = () => {
    setIsClone(true);
    setShowCreateChartNotesModal(true);
  };

  const handleDownloadPdf = async () => {
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 14;
      let y = 20;

      // Title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text("Patient Chart Note", pageWidth / 2, y, { align: "center" });
      y += 15;

      // Patient Info
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Patient Name: ${patientDetail?.name || "N/A"}`, margin, y);
      y += 7;
      // Format DOB as MM-DD-YYYY if available
      let dobText = "N/A";
      if (patientDetail?.dob) {
        const dobDate = new Date(patientDetail.dob);
        if (!isNaN(dobDate.getTime())) {
          const mm = String(dobDate.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(dobDate.getUTCDate()).padStart(2, "0");
          const yyyy = dobDate.getUTCFullYear();
          dobText = `${mm}-${dd}-${yyyy}`;
        }
      }
      pdf.text(`DOB: ${dobText}`, margin, y);
      y += 7;
      pdf.text(`Created: ${createdDate || "N/A"}`, margin, y);
      y += 10;

      // Divider line
      pdf.setDrawColor(200);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 8;

      // Summary Table
      pdf.autoTable({
        startY: y,
        headStyles: { fillColor: [0, 128, 96], textColor: 255 },
        head: [["Field", "Value"]],
        body: [
          ["NPI", getNpiFromName(authorName, chartDetails?.noteType)],
          [
            chartDetails?.noteType === "BELUGA_HEALTH" ? "Master ID" : chartDetails?.referenceType === "BOOKING" ? "Booking ID" : "Kit ID",
            referenceId || "N/A"
          ],
          [chartDetails?.noteType === "BELUGA_HEALTH" ? "Provider Name" : "Author", authorName || "N/A"]
        ],
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 4 },
        margin: { left: margin, right: margin }
      });

      y = pdf.lastAutoTable.finalY + 15;

      // Note Content (with auto-page wrapping)
      pdf.setFont("helvetica", "bold");
      pdf.text("Note:", margin, y);
      y += 8;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      const noteText = noteMessage ? noteMessage.replace(/<\/?[^>]+(>|$)/g, "") : "No notes available.";
      const splitText = pdf.splitTextToSize(noteText, pageWidth - margin * 2);

      splitText.forEach(line => {
        if (y > pageHeight - 20) {
          pdf.addPage();
          y = 20;
        }
        pdf.text(line, margin, y);
        y += 6;
      });

      // Footer only timestamp
      pdf.setFontSize(9);
      pdf.setTextColor(150);
      pdf.text(`${new Date().toLocaleString()}`, pageWidth - margin, pageHeight - 10, {
        align: "right"
      });

      pdf.save(`${patientDetail?.name || "chart"}_note.pdf`);
    } catch (err) {
      console.error("PDF generation failed", err);
    }
  };

  // If search changes, expand if search matches, but don't collapse or override external expansion control otherwise
  useEffect(() => {
    if (!search?.length) return;

    const result = searchInNotes();

    // Only override expansion if match is found
    if (result) {
      setIsExpanded(true);
    }
    // If no match, don’t collapse manually — let external control apply
  }, [search, noteMessage]);

  // Respond to changes in isExpandedProp, always using Boolean for safety
  useEffect(() => {
    setIsExpanded(Boolean(isExpandedProp));
  }, [isExpandedProp]);

  // Helper to normalize a string: lowercase, remove non-letters, trim
  const normalize = str =>
    str
      .toLowerCase()
      .replace(/[^a-z\s]/gi, "")
      .replace(/\s+/g, " ")
      .trim();

  // Check if the normalized noteType contains "beluga"
  const isBeluga = chartDetails?.noteType && normalize(chartDetails.noteType).includes("beluga");

  return (
    <div className={`chartingNoteContent ${isExpanded ? "expanded" : ""}`}>
      <CreateChartNotesModal
        show={showCreateChartNotesModal}
        onHide={() => setShowCreateChartNotesModal(false)}
        patientDetails={patientDetail}
        referenceId={referenceId}
        referenceType={chartDetails?.referenceType}
        visibility={chartDetails?.visibility}
        personId={chartDetails?.personId}
        chartDetails={chartDetails}
        cb={cb}
        isClone={isClone}
      />
      <div>
        <p>NPI</p>
        <h5>{getNpiFromName(authorName, chartDetails?.noteType)}</h5>
      </div>
      <div
        className="borderContent-left"
        style={{
          width: "100%"
        }}
      >
        {isBeluga && referenceId ? (
          <>
            <p>Master ID</p>
            <h5>{referenceId}</h5>
          </>
        ) : chartDetails?.referenceType === "BOOKING" && referenceId ? (
          <>
            <p>Booking ID</p>
            <h5>{referenceId}</h5>
          </>
        ) : referenceId ? (
          <>
            <p>Kit ID</p>
            <h5>{referenceId}</h5>
          </>
        ) : null}
      </div>
      <div className="borderContent-kit">
        <p>{isBeluga ? "Provider Name" : "Author"}</p>
        <h5>
          <span>
            <AiOutlineCheck color="green" />
          </span>{" "}
          {authorName}
        </h5>
      </div>
      <div>
        <p>Created</p>
        <h5>{createdDate}</h5>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start"
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", flexDirection: "column" }}>
          {chartDetails?.noteType === "PATIENT_QUESTIONNAIRE_RESPONSE" && (
            <div
              style={{
                margin: "0.5px 0"
              }}
            >
              <button className="viewDetailsBtn" onClick={onClickCloneNote}>
                <AiFillFileAdd
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Clone Note</span>
              </button>
            </div>
          )}
          {isExpanded ? (
            <div style={{ display: "flex", gap: "10px" }}>
              <button className="viewDetailsBtn" onClick={handleDownloadPdf}>
                <AiOutlineCheck
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Download PDF</span>
              </button>
              <button className="viewDetailsBtn" onClick={() => setIsExpanded(false)}>
                <BsChevronUp
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Close Notes</span>
              </button>
            </div>
          ) : (
            <button className="viewDetailsBtn" onClick={onClickChevronDown}>
              <BsChevronDown
                style={{
                  fontSize: "1.5rem",
                  color: "#007F61",
                  cursor: "pointer"
                }}
              />
              <span className="chartingColour"> View Notes</span>
            </button>
          )}
        </div>
      </div>
      <div className={`${resultData ? "resultData" : "gridSpanFullCol"}`}>
        <div className="resultData">
          {noteMessage ? (
            <div className="resultContainer">
              <p>Note</p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between"
                }}
              >
                {search ? (
                  getHighlightedText(noteMessage, search)
                ) : (
                  <div className="reset-div" dangerouslySetInnerHTML={{ __html: noteMessage }}></div>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                gridColumn: "1 / span 7",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <h1 className="noRecordFound">No Message Found</h1>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
